import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  FileSearch, 
  BookOpen, 
  Camera,
  MessageCircle,
  Clock,
  ArrowRight,
  Trash2,
  ExternalLink,
  Filter,
  ChevronDown,
  Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeConfig = {
  analysis: {
    icon: FileSearch,
    label: 'Analyse de devis',
    color: 'blue',
    bgColor: 'bg-blue-500/10',
    borderColor: 'border-blue-500/20',
    iconColor: 'text-blue-400',
    linkPrefix: '/analyze/'
  },
  recipe: {
    icon: BookOpen,
    label: 'Recette travaux',
    color: 'emerald',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    iconColor: 'text-emerald-400',
    linkPrefix: '/recipes/'
  },
  diagnostic: {
    icon: Camera,
    label: 'Diagnostic photo',
    color: 'purple',
    bgColor: 'bg-purple-500/10',
    borderColor: 'border-purple-500/20',
    iconColor: 'text-purple-400',
    linkPrefix: '/diagnostic/'
  },
  chat: {
    icon: MessageCircle,
    label: 'Conversation',
    color: 'gold',
    bgColor: 'bg-gold-500/10',
    borderColor: 'border-gold-500/20',
    iconColor: 'text-gold-400',
    linkPrefix: '/chat/'
  }
};

const filterOptions = [
  { key: 'all', label: 'Tout' },
  { key: 'analysis', label: 'Devis' },
  { key: 'recipe', label: 'Recettes' },
  { key: 'diagnostic', label: 'Photos' }
];

export default function HistoryPage() {
  const { getAuthHeaders } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/history`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.log('Error fetching history: ' + String(error?.message || error));
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm('Supprimer cet élément ?')) return;
    
    try {
      let endpoint;
      switch (item.type) {
        case 'analysis': endpoint = `/api/analyses/${item.id}`; break;
        case 'recipe': endpoint = `/api/recipes/${item.id}`; break;
        case 'diagnostic': endpoint = `/api/diagnostics/${item.id}`; break;
        default: return;
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setItems(prev => prev.filter(i => !(i.type === item.type && i.id === item.id)));
      }
    } catch (error) {
      console.log('Error deleting: ' + String(error?.message || error));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const itemDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (itemDate.getTime() === today.getTime()) {
      return "Aujourd'hui à " + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (itemDate.getTime() === yesterday.getTime()) {
      return "Hier à " + date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const filteredItems = filter === 'all' 
    ? items 
    : items.filter(item => item.type === filter);

  const currentFilter = filterOptions.find(f => f.key === filter);

  return (
    <div className="min-h-screen bg-black-rich pt-20 pb-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white mb-2">
              Mon historique
            </h1>
            <p className="text-zinc-500">
              Retrouvez toutes vos analyses et recettes
            </p>
          </div>
          
          {/* Filtre */}
          <div className="relative">
            <Button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              variant="outline"
              className="border-zinc-700 text-zinc-300"
            >
              <Filter className="w-4 h-4 mr-2" />
              {currentFilter?.label}
              <ChevronDown className="w-4 h-4 ml-2" />
            </Button>
            
            {showFilterMenu && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute right-0 top-full mt-2 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-10 py-1 min-w-[150px]"
              >
                {filterOptions.map(option => (
                  <button
                    key={option.key}
                    onClick={() => { setFilter(option.key); setShowFilterMenu(false); }}
                    className={`w-full px-4 py-2 text-left text-sm hover:bg-zinc-800 transition-colors ${
                      filter === option.key ? 'text-gold-400' : 'text-zinc-300'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Liste */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-gold-500 animate-spin" />
          </div>
        ) : filteredItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glass-card p-12 text-center"
          >
            <div className="w-16 h-16 mx-auto bg-zinc-800 rounded-2xl flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-zinc-600" />
            </div>
            <h3 className="text-white font-semibold text-lg mb-2">
              Aucun élément
            </h3>
            <p className="text-zinc-500 mb-6">
              {filter === 'all' 
                ? "Vous n'avez pas encore utilisé Léon."
                : `Aucune ${currentFilter?.label.toLowerCase()} pour le moment.`
              }
            </p>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gold-500 hover:bg-gold-400 text-black font-bold rounded-lg transition-colors"
            >
              Commencer
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item, index) => {
              const config = typeConfig[item.type];
              if (!config) return null;
              const Icon = config.icon;
              
              return (
                <motion.div
                  key={`${item.type}-${item.id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`glass-card-dark p-4 border ${config.borderColor} hover:border-opacity-60 transition-all group`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${config.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-6 h-6 ${config.iconColor}`} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${config.bgColor} ${config.iconColor}`}>
                          {config.label}
                        </span>
                        <span className="text-zinc-600 text-xs">
                          {formatDate(item.created_at)}
                        </span>
                      </div>
                      <h3 className="text-white font-medium truncate">
                        {item.title || item.name || 'Sans titre'}
                      </h3>
                      {item.subtitle && (
                        <p className="text-zinc-500 text-sm truncate">{item.subtitle}</p>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link
                        to={`${config.linkPrefix}${item.id}`}
                        className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-colors"
                        title="Voir"
                      >
                        <ExternalLink className="w-5 h-5" />
                      </Link>
                      <button
                        onClick={() => deleteItem(item)}
                        className="p-2 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

      </div>
    </div>
  );
}
